import { Reducer } from 'redux';

import { learningPathActionTypes, TILE_STATUSES } from '@/pages/learningPath/Constants';
import { learningPathHasGameStep } from '@/services/Constants';
import { finishTileProgress, updateTileProgressStatus } from '@/pages/learningPath/Helper';

import type { LearningPathAction, LearningPathState } from '@/pages/learningPath/types';

const initialState: LearningPathState = {
	loading: true,
	learningPathUpdated: false,
	steps: [],
	newSteps: [],
	subcategories: [],
	finishTileAnimation: false,
	skipTileAnimation: false,
	shorterLearningPathAnimation: false,
	rewardUserSheetId: null,
	tileSelectionMode: false,
	selectedTile: false,
	previewMode: false,
	tilesToReplace: [],
	autonomyMode: false,
	surveyCode: null,
	finishedDailyGoal: false,
	hasGameStep: false,
};

const learningPathReducer: Reducer<LearningPathState, LearningPathAction> = (
	state = initialState,
	action,
) => {
	const { type, payload } = action;

	const {
		data,
		isUpdated,
		stepId,
		isCorrect,
		finishTileAnimation,
		tileSelectionMode,
		rewardUserSheetId,
		shorterLearningPathAnimation,
		skipTileAnimation,
		selectedTile,
		newSteps,
		steps,
		previewMode,
		tilesToReplace,
		autonomyMode,
		surveyCode,
	} = payload || {};

	switch (type) {
		case learningPathActionTypes.GET_LEARNING_PATH_REQUEST:
			return {
				...state,
				loading: true,
			};

		case learningPathActionTypes.GET_LEARNING_PATH_SUCCESS:
			const dailyGoalTile = data?.learning_path?.find((step) => step.is_daily_goal);
			return {
				...state,
				finishedDailyGoal: dailyGoalTile
					? dailyGoalTile.status === TILE_STATUSES.TILE_COMPLETED
					: false,
				steps: data?.learning_path || [],
				subcategories: data?.subcategories || [],
				loading: false,
				learningPathUpdated: false,
				hasGameStep: learningPathHasGameStep(data?.learning_path),
			};

		case learningPathActionTypes.GET_LEARNING_PATH_ERROR:
			return {
				...state,
				loading: false,
				learningPathUpdated: true,
			};

		case learningPathActionTypes.UPDATE_LEARNING_PATH:
			return {
				...state,
				steps: [...(steps || [])],
			};

		case learningPathActionTypes.UPDATE_TILE_PROGRESS_STATUS:
			return {
				...state,
				steps: updateTileProgressStatus(stepId!, isCorrect!, state.steps),
			};

		case learningPathActionTypes.FINISH_TILE_PROGRESS:
			return {
				...state,
				steps: finishTileProgress(stepId!, state.steps),
			};

		case learningPathActionTypes.SET_LEARNING_PATH_UPDATED:
			return {
				...state,
				learningPathUpdated: isUpdated || false,
			};

		case learningPathActionTypes.SET_FINISH_TILE_ANIMATION:
			return {
				...state,
				finishTileAnimation: finishTileAnimation || false,
			};

		case learningPathActionTypes.SET_REWARD_USER_SHEET_ID:
			return {
				...state,
				rewardUserSheetId: rewardUserSheetId || null,
			};

		case learningPathActionTypes.SET_TILE_SELECTION_MODE:
			return {
				...state,
				tileSelectionMode: tileSelectionMode || false,
			};

		case learningPathActionTypes.SET_SHORTER_LEARNING_PATH_ANIMATION:
			return {
				...state,
				shorterLearningPathAnimation: shorterLearningPathAnimation || false,
			};

		case learningPathActionTypes.SET_SKIP_TILE_ANIMATION:
			return {
				...state,
				skipTileAnimation: skipTileAnimation || false,
			};

		case learningPathActionTypes.SET_SELECTED_TILE:
			return {
				...state,
				selectedTile: selectedTile || false,
			};

		case learningPathActionTypes.SET_CURRENT_STEPS:
			return {
				...state,
				steps: steps || [],
			};

		case learningPathActionTypes.SET_NEW_STEPS:
			return {
				...state,
				newSteps: newSteps || [],
			};

		case learningPathActionTypes.SET_TILES_TO_REPLACE:
			return {
				...state,
				tilesToReplace: tilesToReplace || [],
			};

		case learningPathActionTypes.SET_PREVIEW_MODE:
			return {
				...state,
				previewMode: previewMode || false,
			};

		case learningPathActionTypes.CLEAR_LEARNING_PATH:
			return {
				...initialState,
				learningPathUpdated: true,
			};

		case learningPathActionTypes.RECEIVED_LEARNING_PATH_CHANNEL_STREAM:
			return {
				...state,
				loading: true,
			};

		case learningPathActionTypes.RECEIVED_LEARNING_PATH_CHANNEL_STREAM_SUCCESS:
			return {
				...state,
				steps: data?.learning_path || [],
				subcategories: data?.subcategories || [],
				loading: false,
				learningPathUpdated: false,
				hasGameStep: learningPathHasGameStep(data?.learning_path),
			};

		case learningPathActionTypes.SET_AUTONOMY_MODE:
			return {
				...state,
				autonomyMode,
			};

		case learningPathActionTypes.SET_TYPEFORM_SURVEY:
			return {
				...state,
				surveyCode,
			};

		default:
			return state;
	}
};

export default learningPathReducer;
